import type {
  ClientFindBookingScheduleQuery,
  PackageDetailFragmentFragment,
} from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  PackageDetailSessionEnum,
  PackageRecurrenceEnum,
  PackageServiceTypeEnum,
  PackageUnitTypeEnum,
  ScheduleJobFrequencyEnum,
} from "@/__generated__/graphql";
import { formatDate, shortDayByIndex, timeFormat } from "@/lib/helpers/date";
import type {
  Portfolio,
  TimeSlot,
  WorkerPortfolio,
  WorkerPortfolio as WorkerPortfolioQuery,
} from "@/types/booking";
import { PackageFrequencyGroupEnum } from "@/types/booking";
import type { ServiceName, UpholsteryServiceName } from "@/types/service";
import { UpholsteryServiceNames } from "@/types/service";
import {
  addDays,
  addHours,
  addMonths,
  isBefore,
  isSameDay,
  isWithinInterval,
} from "date-fns";

export function groupTimeSlotByDate(
  timeSlots: TimeSlot[],
): Record<string, TimeSlot[]> {
  return timeSlots.reduce<Record<string, TimeSlot[]>>((group, timeslot) => {
    if (group[timeslot.date]) {
      group[timeslot.date].push(timeslot);
    } else {
      group[timeslot.date] = [timeslot];
    }

    return group;
  }, {});
}

export function transformScheduleVariantToTimeSlot(
  scheduleVariants: ClientFindBookingScheduleQuery["clientFindBookingSchedule"],
  isRecurring: boolean,
): TimeSlot[] {
  const now = new Date();
  const fiveHoursFromNow = addHours(now, 5);

  return scheduleVariants.reduce<TimeSlot[]>((allTimeSlot, scheduleVariant) => {
    scheduleVariant.morning?.forEach((item, i) => {
      const startDate = new Date(item.startAt);
      const endDate = new Date(item.endAt);

      const isToday = isSameDay(startDate, now);
      const isWithinFiveHours =
        isToday && isBefore(startDate, fiveHoursFromNow);

      if (isWithinFiveHours) return;

      allTimeSlot.push({
        startAt: item.startAt,
        id: `${scheduleVariant.day}-morning-${i}`,
        slotId: item.id,
        date: formatDate(startDate),
        day: shortDayByIndex(scheduleVariant.day),
        partOfDay: "Morning",
        startTime: formatDate(startDate, timeFormat),
        endTime: formatDate(endDate, timeFormat),
        averageTravelTime: item.averageTravelTime,
        workerId: item.worker.id,
        avatarUrl: item.worker.avatarUrl ?? "",
        workerRating: item.worker.workerRating,
        workerName: `${item.worker.firstName} ${item.worker.lastName}`,
        portfolios: getPortfolios(item.worker.workerContracts ?? []),
        isCheaper: !isRecurring && isSameDay(startDate, addDays(new Date(), 1)),
        discount: undefined, // TODO: fix this, our schedule query doesnt have item.discount
        rateValue: item.rateValue ?? undefined,
        workerPortfolios: getWorkerPortfolios(
          item.worker.workerPortfolios ?? [],
        ),
      });
    });

    scheduleVariant.afternoon?.forEach((item, i) => {
      const startDate = new Date(item.startAt);
      const endDate = new Date(item.endAt);

      const isToday = isSameDay(startDate, now);
      const isWithinFiveHours =
        isToday && isBefore(startDate, fiveHoursFromNow);

      if (isWithinFiveHours) return;

      allTimeSlot.push({
        startAt: item.startAt,
        id: `${scheduleVariant.day}-afternoon-${i}`,
        slotId: item.id,
        date: formatDate(startDate),
        day: shortDayByIndex(scheduleVariant.day),
        partOfDay: "Afternoon",
        startTime: formatDate(startDate, timeFormat),
        endTime: formatDate(endDate, timeFormat),
        averageTravelTime: item.averageTravelTime,
        workerId: item.worker.id,
        avatarUrl: item.worker.avatarUrl ?? "",
        workerRating: item.worker.workerRating,
        workerName: `${item.worker.firstName} ${item.worker.lastName}`,
        portfolios: getPortfolios(item.worker.workerContracts ?? []),
        isCheaper: !isRecurring && isSameDay(startDate, addDays(new Date(), 1)),
        discount: undefined, // TODO: fix this, our schedule query doesnt have item.discount
        rateValue: item.rateValue ?? undefined,
        workerPortfolios: getWorkerPortfolios(
          item.worker.workerPortfolios ?? [],
        ),
      });
    });

    scheduleVariant.evening?.forEach((item, i) => {
      const startDate = new Date(item.startAt);
      const endDate = new Date(item.endAt);

      const isToday = isSameDay(startDate, now);
      const isWithinFiveHours =
        isToday && isBefore(startDate, fiveHoursFromNow);

      if (isWithinFiveHours) return;

      allTimeSlot.push({
        startAt: item.startAt,
        id: `${scheduleVariant.day}-evening-${i}`,
        slotId: item.id,
        date: formatDate(startDate),
        day: shortDayByIndex(scheduleVariant.day),
        partOfDay: "Evening",
        startTime: formatDate(startDate, timeFormat),
        endTime: formatDate(endDate, timeFormat),
        averageTravelTime: item.averageTravelTime,
        workerId: item.worker.id,
        avatarUrl: item.worker.avatarUrl ?? "",
        workerRating: item.worker.workerRating,
        workerName: `${item.worker.firstName} ${item.worker.lastName}`,
        portfolios: getPortfolios(item.worker.workerContracts ?? []),
        isCheaper: true, // All night slots are cheaper
        discount: undefined, // TODO: fix this, our schedule query doesnt have item.discount
        rateValue: item.rateValue ?? undefined,
        workerPortfolios: getWorkerPortfolios(
          item.worker.workerPortfolios ?? [],
        ),
      });
    });

    return allTimeSlot;
  }, []);
}

type WorkerContractPorto = NonNullable<
  NonNullable<
    ClientFindBookingScheduleQuery["clientFindBookingSchedule"][0]["morning"]
  >[0]["worker"]["workerContracts"]
>[0];

export const getPortfolios = (
  workerContracts: WorkerContractPorto[],
): Portfolio[] => {
  const workerPortfolios = workerContracts.reduce<Portfolio[]>(
    (workerPortfolios, contracts) => {
      const itemPortfolios: Portfolio[] = contracts.portfolios
        ? contracts.portfolios.map((itemPortfolio) => {
            return {
              id: itemPortfolio.id,
              fileName: itemPortfolio.filename,
              imgUrl: itemPortfolio.downloadUrl,
            };
          })
        : [];

      return [...workerPortfolios, ...itemPortfolios];
    },
    [],
  );

  return workerPortfolios;
};

export const getWorkerPortfolios = (
  workerPortfolios?: WorkerPortfolioQuery[],
): WorkerPortfolio[] => {
  if (!workerPortfolios) {
    return [];
  }

  return workerPortfolios.map((portfolio) => ({
    id: portfolio.id,
    description: portfolio.description,
  }));
};

export function getRepeatEveryFrequency(
  frequency: PackageFrequencyGroupEnum,
): [PackageRecurrenceEnum[] | undefined, number | undefined] {
  switch (frequency) {
    case (PackageFrequencyGroupEnum.AD_HOC,
    PackageFrequencyGroupEnum.NEXT_DAY_AD_HOC):
      return [[PackageRecurrenceEnum.AdHoc], 0];
    case PackageFrequencyGroupEnum.WEEKLY:
      return [[PackageRecurrenceEnum.Week], 1];
    case PackageFrequencyGroupEnum.FORTNIGHT:
      return [[PackageRecurrenceEnum.Fortnight], 1];
    case PackageFrequencyGroupEnum.TRI_YEARLY:
      return [[PackageRecurrenceEnum.Year], 3];
    case PackageFrequencyGroupEnum.QUARTERLY:
      return [[PackageRecurrenceEnum.Year], 4];
    default:
      return [undefined, undefined];
  }
}

export function getDateRangeFromFrequency(
  frequency: PackageFrequencyGroupEnum,
): [Date, Date] {
  const todayDate = new Date();
  const tomorrowDate = addDays(new Date(), 1);

  switch (frequency) {
    case PackageFrequencyGroupEnum.AD_HOC:
      return [todayDate, addDays(todayDate, 6)];
    case PackageFrequencyGroupEnum.NEXT_DAY_AD_HOC:
      return [todayDate, addDays(todayDate, 1)];
    case PackageFrequencyGroupEnum.WEEKLY:
    case PackageFrequencyGroupEnum.FORTNIGHT:
      return [tomorrowDate, addDays(tomorrowDate, 6)];
    default:
      return [todayDate, addDays(todayDate, 6)];
  }
}

export function getServiceDepartments(name: ServiceName) {
  switch (name) {
    case "home-cleaning":
      return PackageDepartmentEnum.HomeCleaning;
    case "aircon-service":
      return PackageDepartmentEnum.Aircon;
    case "home-beauty":
      return PackageDepartmentEnum.HomeBeauty;
    case "mattress-cleaning":
    case "rug-cleaning":
    case "sofa-cleaning":
    case "pram-seat":
      return PackageDepartmentEnum.DeepCleaning; // TODO: will update this again after product aligning service name
    default:
      return PackageDepartmentEnum.HomeCleaning;
  }
}
export const getServiceNameFromDepartment = (
  department: PackageDepartmentEnum,
): ServiceName => {
  switch (department) {
    case PackageDepartmentEnum.HomeCleaning:
      return "home-cleaning";
    case PackageDepartmentEnum.Aircon:
      return "aircon-service";
    case PackageDepartmentEnum.HomeBeauty:
      return "home-beauty";
    case PackageDepartmentEnum.Massage:
      return "home-massage";
    case PackageDepartmentEnum.PetCare:
      return "pet-grooming";
    case PackageDepartmentEnum.ElderCare:
      return "elder-care";
    case PackageDepartmentEnum.DeepCleaning:
      return "deep-cleaning";
    case PackageDepartmentEnum.CarpetUpholstery:
      return "rug-cleaning";
    default:
      return "home-cleaning";
  }
};

export const getBookingPackages = (
  department: PackageDepartmentEnum,
  packageDetails: PackageDetailFragmentFragment[],
  selectedSlot?: TimeSlot | null,
) => {
  if (department === PackageDepartmentEnum.HomeCleaning) {
    if (selectedSlot) {
      return packageDetails.filter(({ session }) => {
        const sessionSlot =
          selectedSlot.partOfDay === "Evening"
            ? PackageDetailSessionEnum.Evening
            : PackageDetailSessionEnum.Day;

        return session === sessionSlot;
      });
    } else {
      return packageDetails.filter(
        ({ session }) => session === PackageDetailSessionEnum.Day,
      );
    }
  }
  return packageDetails;
};

const DISCOUNT_UPHOLSTERY_TWO_UNITS = 5;
const DISCOUNT_UPHOLSTERY_THREE_UNITS = 10;
const DISCOUNT_UPHOLSTERY_MORE_THAN_THREE_UNITS = 15;
const ELIGIBLE_DEEP_CLEANING_UNIT_TYPES_FOR_DISCOUNT = [
  PackageUnitTypeEnum.Mattress,
  PackageUnitTypeEnum.Sofa,
  PackageUnitTypeEnum.Rug,
  PackageUnitTypeEnum.ChildSeat,
  PackageUnitTypeEnum.BabyPram,
];

export function calculateServiceDiscount(
  totalUnit: number,
  totalBillingValue: number,
  department: PackageDepartmentEnum,
  unitTypes: PackageUnitTypeEnum[],
  serviceType: PackageServiceTypeEnum,
): { percent: number; total: number } {
  let percentDiscount = 0;
  let totalDiscount = 0;

  const deepCleaningServiceType =
    serviceType === PackageServiceTypeEnum.DeepCleaning;

  if (deepCleaningServiceType) {
    percentDiscount = 0;
  }

  if (
    !deepCleaningServiceType &&
    department === PackageDepartmentEnum.DeepCleaning &&
    ELIGIBLE_DEEP_CLEANING_UNIT_TYPES_FOR_DISCOUNT.some((unitType) =>
      unitTypes.includes(unitType),
    )
  ) {
    if (totalUnit > 3) {
      percentDiscount = DISCOUNT_UPHOLSTERY_MORE_THAN_THREE_UNITS;
    } else if (totalUnit === 3) {
      percentDiscount = DISCOUNT_UPHOLSTERY_THREE_UNITS;
    } else if (totalUnit === 2) {
      percentDiscount = DISCOUNT_UPHOLSTERY_TWO_UNITS;
    }

    totalDiscount = (percentDiscount * totalBillingValue) / 100;
  }

  return { percent: percentDiscount, total: totalDiscount };
}

export const getReviewServiceDepartment = (
  name: ServiceName,
): {
  department: PackageDepartmentEnum;
  serviceType?: PackageServiceTypeEnum;
} => {
  if (name === "lashes")
    return {
      department: PackageDepartmentEnum.HomeBeauty,
      serviceType: PackageServiceTypeEnum.Lashes,
    };
  return { department: getDepartment(name) };
};

export const getDepartment = (name: ServiceName) => {
  if (UpholsteryServiceNames.includes(name as UpholsteryServiceName)) {
    return PackageDepartmentEnum.DeepCleaning;
  }
  switch (name) {
    case "home-cleaning":
      return PackageDepartmentEnum.HomeCleaning;
    case "aircon-service":
      return PackageDepartmentEnum.Aircon;
    case "home-beauty":
      return PackageDepartmentEnum.HomeBeauty;
    case "home-massage":
      return PackageDepartmentEnum.Massage;
    case "pet-grooming":
    case "pet-sitting":
      return PackageDepartmentEnum.PetCare;
    case "lashes":
      return PackageDepartmentEnum.HomeBeauty;
    case "elder-care":
      return PackageDepartmentEnum.ElderCare;
    case "deep-cleaning":
      return PackageDepartmentEnum.DeepCleaning;
    // still un hold pending on vertical lead babysitter
    // case "babysitter":
    //   return PackageDepartmentEnum.Babysitter;
    default:
      return PackageDepartmentEnum.HomeCleaning;
  }
};

export function getSelectedPackagesFromIds(
  selectedPackageIds: string[],
  packageDetails: PackageDetailFragmentFragment[],
) {
  return selectedPackageIds.map(
    (id) =>
      packageDetails.find(
        (pkg) => pkg.id === id,
      ) as PackageDetailFragmentFragment,
  );
}

export function getWorkerSkillIdsFromPackages(
  packageDetails: PackageDetailFragmentFragment[],
) {
  return [...new Set(packageDetails.map((pkg) => pkg.workerSkill.id))];
}

// TODO: will delete this function after BE optimize `frequencyOfJob` input;
export const getFrequencyOfJobFromRecurrence = (
  recurrence: PackageRecurrenceEnum,
) => {
  switch (recurrence) {
    case PackageRecurrenceEnum.AdHoc:
    case PackageRecurrenceEnum.Day:
    case PackageRecurrenceEnum.Month:
      return ScheduleJobFrequencyEnum.AdHoc;
    case PackageRecurrenceEnum.Fortnight:
      return ScheduleJobFrequencyEnum.Fortnight;
    case PackageRecurrenceEnum.Week:
      return ScheduleJobFrequencyEnum.Weekly;
    case PackageRecurrenceEnum.Year:
      return ScheduleJobFrequencyEnum.Year;
  }
};

export const getServiceUnit = (name: ServiceName) => {
  switch (name) {
    case "home-cleaning":
      return "hr(s)";
    case "aircon-service":
      return "unit(s)";
    case "mattress-cleaning":
    case "pram-seat":
    case "rug-cleaning":
    case "sofa-cleaning":
      return "item(s)";
    case "curtain-cleaning":
      return "piece(s)";
    case "home-beauty":
    case "pet-grooming":
    case "pet-sitting":
      return "session(s)";
    default:
      return "unit(s)";
  }
};

export const isReadyToSchedule = (serviceDate: Date): boolean => {
  const oneMonthBeforeVisitStart = addMonths(serviceDate, -1);
  return isWithinInterval(new Date(), {
    start: oneMonthBeforeVisitStart,
    end: serviceDate,
  });
};

export const timeRanges = [
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 0,
  },
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 1,
  },
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 2,
  },
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 3,
  },
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 4,
  },
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 5,
  },
  {
    startTime: "06:00",
    endTime: "22:00",
    day: 6,
  },
];
